import { useEffect, useState, useRef } from 'react';
import styled from 'styled-components/macro';
import classNames from 'classnames';
import { TechSector } from '../../config/tech-sector';
import useTranslations from '../utils/language-provider';
import TechSectorIcon from './tech-sector-icon';
import { BuildingType } from '../../config/buildings';
import { Link, useParams } from 'react-router-dom';
import { formatBuildingRoute } from '../utils/router';
import { RouteParams } from '../../interfaces/router';
import { buildings } from '../../config/building-config';
import NewFlag from './newflag';
import { isMobileDevice } from '../utils/mobile-detection';

type BuildingNavigationProps = {
    buildingType: BuildingType;
}

const NavigationWrapper = styled.div<{ isArea?: boolean }>`
    position: absolute;
    z-index: ${props => props.theme.zIndex.overlay};
    bottom: 0px;
    font-size: ${props => props.theme.typography.sm};
    height: 40px;
    width: 100%;

    @media ${props => props.theme.screens.lg} {
        font-size: ${props => props.theme.typography.md};
        height: 75px;
        bottom: -65px;
    }
`

const Inner = styled.div`
    background: ${(props) => props.theme.colors.primary};
    display: flex;
    height: 100%;
`;

const StyledTechSectorButton = styled(TechSectorIcon)<{ newSector?: boolean }>`
    z-index: ${(props) => props.theme.zIndex.dropdown};
    opacity: 0;
    height: ${isMobileDevice() ? '100%' : '120%'};
    width: ${isMobileDevice() ? 'unset' : '100%'};
    box-shadow: none;
    gap: 5px;
    top: ${isMobileDevice() ? '0' : '-20%'};
    align-items: center;

    & svg {
        max-height: 30px;
        stroke-width: 0.1;

        @media ${(props) => props.theme.screens.lg} {
            stroke-width: 0.7;
        }
    }

    &.active {
        > div {
            height: 100%;
            @media ${(props) => props.theme.screens.lg} {
                height: 115px;
            }
        }
    }

    @media ${(props) => props.theme.screens.lg} {
        height: 150%;
        top: -50%;
        & svg {
            max-height: unset;
        }
    }
`;

const TechSectorList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    width: 100%;

    @media ${(props) => props.theme.screens.lg} {
        display: flex;
        flex-direction: row;
    }

    &.active {
        ${StyledTechSectorButton} {
            opacity: 1;
        }
    }
`;

const RelativeLink = styled(Link)`
    text-decoration: none;
    color: black;
    align-items: center;
    @media ${(props) => props.theme.screens.lg} {
        position: relative;
        flex-grow: 1;
        align-items: end;
    }
`;

const BuildingNavigation = ({
    buildingType,
    ...props
}: BuildingNavigationProps) => {
    const translations = useTranslations();
    const [sectorButtonsVisible, setSectorButtonsVisible] = useState(false);
    const {techSector: routeTechSector} = useParams<RouteParams>();
    const [selectedTechSector, setSelectedTechSector] = useState<TechSector|undefined>(routeTechSector);
    const parentBuilding = buildings[buildingType].parentBuilding;
    const sectors = parentBuilding !== undefined ? buildings[parentBuilding].techSectors : buildings[buildingType].techSectors;
    const building = parentBuilding ?? buildingType;

    const animationKey = `navigation-${buildingType}`;
    const hasAnimated = sessionStorage.getItem(animationKey) === 'true';

    useEffect(() => {
        if (!hasAnimated) {
            setTimeout(() => {
                setSectorButtonsVisible(true);
                sessionStorage.setItem(animationKey, 'true');
            }, 500);
        } else {
            setSectorButtonsVisible(true);
        }
    }, [animationKey, hasAnimated])

    useEffect(() => {
        setSelectedTechSector(routeTechSector);
    }, [routeTechSector])

    const handleTechSectorMouseOver = (sectorType: TechSector) => {
        if (sectorType !== selectedTechSector) {
            setSelectedTechSector(sectorType);
        }
    }

    const handleTechSectorMouseOut = () => {
        setSelectedTechSector(routeTechSector);
    }

    return (
        <NavigationWrapper {...props} isArea={sectors?.some(sector => sector.buildings)}>
            <Inner>
                <TechSectorList className={classNames({active: sectorButtonsVisible})}>
                    {sectors?.map((sector, index) => {
                        const btnProps = {
                            sectorType: sector.type,
                            style: {
                                transitionDelay: `${100 * index}ms`
                            }
                        }

                        if (sector.comingSoon) {
                            return (
                                <StyledTechSectorButton
                                    key={sector.type}
                                    {...btnProps}
                                    inactive
                                    buildingNav
                                />
                            );
                        }

                        return (
                            <RelativeLink key={sector.type} to={formatBuildingRoute({building: building, techSector: sector.type})}>
                                <StyledTechSectorButton
                                    {...btnProps}
                                    newSector = {sector.newSector}
                                    className={classNames({active: routeTechSector === sector.type})}
                                    onHover={[
                                        () => handleTechSectorMouseOver(sector.type),
                                        handleTechSectorMouseOut
                                    ]}
                                    buildingNav
                                />
                                {sector.newSector &&
                                    <NewFlag type='tech-sector-building' animationDelay={index}>
                                        {translations.buildingDetails.new}
                                    </NewFlag>
                                }
                            </RelativeLink>
                        )
                    })}
                </TechSectorList>
            </Inner>
        </NavigationWrapper>
    )
}

export default BuildingNavigation;